/**
 * BilanComparePage - Comparison view between two accounting statements
 *
 * Displays side-by-side comparison with differences and percentages.
 */

import { useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilanStore } from '../store/bilanStore';
import { cn } from '../utils/cn';

const BilanComparePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const id1 = searchParams.get('id1');
  const id2 = searchParams.get('id2');

  const {
    comparison,
    isLoadingComparison,
    error,
    compareStatements,
    exportComparisonPdf,
    clearComparison,
    clearError,
  } = useBilanStore();

  useEffect(() => {
    if (id1 && id2) {
      compareStatements(id1, id2);
    } else {
      navigate('/bilans');
    }

    return () => {
      clearComparison();
    };
  }, [id1, id2, compareStatements, navigate, clearComparison]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const formatPercentage = (value: number | null) => {
    if (value === null) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const handleExportPdf = async () => {
    if (!id1 || !id2) return;
    const blob = await exportComparisonPdf(id1, id2);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparison_bilans.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (isLoadingComparison) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">{t('bilans.comparisonNotFound')}</p>
        <Link
          to="/bilans"
          className="mt-4 inline-block text-violet-600 hover:text-violet-700"
        >
          ← {t('common.back')}
        </Link>
      </div>
    );
  }

  const { statement1, statement2, categoryComparisons } = comparison;
  const expenseComparisons = categoryComparisons.filter((c) => c.baseType === 'debit');
  const incomeComparisons = categoryComparisons.filter((c) => c.baseType === 'credit');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link
            to="/bilans"
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 mb-2 inline-block"
          >
            ← {t('bilans.backToList')}
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('bilans.comparisonTitle')}
          </h1>
        </div>
        <button
          onClick={handleExportPdf}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-lg transition-colors"
        >
          <DownloadIcon className="w-4 h-4" />
          {t('bilans.exportPdf')}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Statements info */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-4">
          <h3 className="font-semibold text-violet-700 dark:text-violet-300 mb-2">
            {statement1.name}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {formatDate(statement1.startDate)} - {formatDate(statement1.endDate)}
          </p>
        </div>
        <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-4">
          <h3 className="font-semibold text-violet-700 dark:text-violet-300 mb-2">
            {statement2.name}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {formatDate(statement2.startDate)} - {formatDate(statement2.endDate)}
          </p>
        </div>
      </div>

      {/* Expenses comparison */}
      {expenseComparisons.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <h3 className="font-semibold text-red-700 dark:text-red-400">
              {t('bilans.expenses')}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {t('bilans.category')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {statement1.name}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {statement2.name}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {t('bilans.difference')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {t('bilans.evolution')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {expenseComparisons.map((cat) => (
                  <tr key={cat.categoryId}>
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                      {cat.categoryName}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">
                      -{formatAmount(cat.amount1)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">
                      -{formatAmount(cat.amount2)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-sm text-right font-medium',
                        cat.difference > 0
                          ? 'text-red-600 dark:text-red-400'
                          : cat.difference < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-slate-500'
                      )}
                    >
                      {cat.difference > 0 ? '+' : ''}
                      {formatAmount(cat.difference)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-sm text-right font-medium',
                        cat.percentageChange !== null && cat.percentageChange > 0
                          ? 'text-red-600 dark:text-red-400'
                          : cat.percentageChange !== null && cat.percentageChange < 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-slate-500'
                      )}
                    >
                      {formatPercentage(cat.percentageChange)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-red-50 dark:bg-red-900/20">
                <tr>
                  <td className="px-4 py-3 font-semibold text-red-700 dark:text-red-400">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-700 dark:text-red-400">
                    -{formatAmount(statement1.totalExpenses)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-700 dark:text-red-400">
                    -{formatAmount(statement2.totalExpenses)}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 text-right font-bold',
                      comparison.expensesDifference > 0
                        ? 'text-red-700 dark:text-red-400'
                        : 'text-green-700 dark:text-green-400'
                    )}
                  >
                    {comparison.expensesDifference > 0 ? '+' : ''}
                    {formatAmount(comparison.expensesDifference)}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 text-right font-bold',
                      comparison.expensesPercentageChange !== null &&
                        comparison.expensesPercentageChange > 0
                        ? 'text-red-700 dark:text-red-400'
                        : 'text-green-700 dark:text-green-400'
                    )}
                  >
                    {formatPercentage(comparison.expensesPercentageChange)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Income comparison */}
      {incomeComparisons.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-4 py-3 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
            <h3 className="font-semibold text-green-700 dark:text-green-400">
              {t('bilans.income')}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {t('bilans.category')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {statement1.name}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {statement2.name}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {t('bilans.difference')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    {t('bilans.evolution')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {incomeComparisons.map((cat) => (
                  <tr key={cat.categoryId}>
                    <td className="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                      {cat.categoryName}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400">
                      +{formatAmount(cat.amount1)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-green-600 dark:text-green-400">
                      +{formatAmount(cat.amount2)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-sm text-right font-medium',
                        cat.difference > 0
                          ? 'text-green-600 dark:text-green-400'
                          : cat.difference < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-slate-500'
                      )}
                    >
                      {cat.difference > 0 ? '+' : ''}
                      {formatAmount(cat.difference)}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 text-sm text-right font-medium',
                        cat.percentageChange !== null && cat.percentageChange > 0
                          ? 'text-green-600 dark:text-green-400'
                          : cat.percentageChange !== null && cat.percentageChange < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-slate-500'
                      )}
                    >
                      {formatPercentage(cat.percentageChange)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-green-50 dark:bg-green-900/20">
                <tr>
                  <td className="px-4 py-3 font-semibold text-green-700 dark:text-green-400">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 dark:text-green-400">
                    +{formatAmount(statement1.totalIncome)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 dark:text-green-400">
                    +{formatAmount(statement2.totalIncome)}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 text-right font-bold',
                      comparison.incomeDifference > 0
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-red-700 dark:text-red-400'
                    )}
                  >
                    {comparison.incomeDifference > 0 ? '+' : ''}
                    {formatAmount(comparison.incomeDifference)}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 text-right font-bold',
                      comparison.incomePercentageChange !== null &&
                        comparison.incomePercentageChange > 0
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-red-700 dark:text-red-400'
                    )}
                  >
                    {formatPercentage(comparison.incomePercentageChange)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Net result comparison */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border-2 border-violet-200 dark:border-violet-800 overflow-hidden">
        <div className="px-4 py-3 bg-violet-50 dark:bg-violet-900/20 border-b border-violet-200 dark:border-violet-800">
          <h3 className="font-semibold text-violet-700 dark:text-violet-400">
            {t('bilans.netResult')}
          </h3>
        </div>
        <div className="p-6 grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
              {statement1.name}
            </p>
            <p
              className={cn(
                'text-xl font-bold',
                statement1.netResult >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}
            >
              {statement1.netResult >= 0 ? '+' : ''}
              {formatAmount(statement1.netResult)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
              {statement2.name}
            </p>
            <p
              className={cn(
                'text-xl font-bold',
                statement2.netResult >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}
            >
              {statement2.netResult >= 0 ? '+' : ''}
              {formatAmount(statement2.netResult)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
              {t('bilans.difference')}
            </p>
            <p
              className={cn(
                'text-xl font-bold',
                comparison.netResultDifference >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}
            >
              {comparison.netResultDifference >= 0 ? '+' : ''}
              {formatAmount(comparison.netResultDifference)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
              {t('bilans.evolution')}
            </p>
            <p
              className={cn(
                'text-xl font-bold',
                comparison.netResultPercentageChange !== null &&
                  comparison.netResultPercentageChange >= 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              )}
            >
              {formatPercentage(comparison.netResultPercentageChange)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default BilanComparePage;
